import React, { useEffect, useState } from 'react'

import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { cepMask } from '@utils/masks'
import { Option } from '@interco/inter-ui/components/BottomSheet/SelectBottomSheet/types'
import { ApplicationState } from '@store/types'
import { Button } from '@interco/inter-ui/components/Button'
import { Page } from '@templates'
import { ColorWrapper, TextParagraph, Loading } from '@atoms'
import { Colors } from '@utils/Colors'
import Select from '@molecules/Select'
import Tag from '@utils/Tag'
import { buildTipoEndereco, transformTipoEnderecoToOption } from '@utils/transformers'
import {
  setCep,
  setTipoLogradouro,
  setLogradouro,
  setBairro,
  setNumero,
  setComplemento,
  setUf,
  setCidade,
  enderecoRequest,
} from '@store/refinanciamento/endereco/actions'
import { setHasEndereco } from '@store/refinanciamento/dadosPessoais/actions'
import { trackingRefin } from '@store/refinanciamento/simulacao/actions'
import { StepRefin } from '@utils/enums'
import { Endereco as EnderecoType } from '@store/refinanciamento/endereco/types'
import routes from '@routes/routes'
import { getNextRoute } from '@store/refinanciamento/dadosPessoais/utils'
import { cleanCep } from '@utils/functions'

import { Container, CustomInput } from './styles'

interface EnderecoLocal extends EnderecoType {
  numero: string
  complemento: string
}

const Endereco = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const {
    cep,
    enderecoLoading: loading,
    tipoLogradouro,
    logradouro,
    bairro,
    numero,
    complemento,
    uf,
    cidade,
  } = useSelector((state: ApplicationState) => state.refinanciamento.endereco, shallowEqual)

  const [showAddressForm, setShowAddressForm] = useState(Boolean(cep))

  const [localEndereco, setLocalEndereco] = useState<EnderecoLocal>({
    cep: cep || '',
    tipoLogradouro: tipoLogradouro || '',
    logradouro: logradouro || '',
    bairro: bairro || '',
    numero: numero || '',
    complemento: complemento || '',
    uf: uf || '',
    cidade: cidade || '',
  })

  const [disabledFields, setDisabledFields] = useState({
    tipoLogradouro: Boolean(tipoLogradouro),
    logradouro: Boolean(logradouro),
    bairro: Boolean(bairro),
    uf: Boolean(uf),
    cidade: Boolean(cidade),
  })

  const fromInformacoesPessoais = location.state?.fromInformacoesPessoais || false

  const isConfirmButtonDisabled = (): boolean =>
    !(
      localEndereco.cep.length >= 10 &&
      localEndereco.tipoLogradouro &&
      localEndereco.logradouro &&
      localEndereco.bairro &&
      localEndereco.uf &&
      localEndereco.cidade &&
      localEndereco.numero
    )

  const handleConfirmClick = () => {
    dispatch(trackingRefin(StepRefin.CLICK_ENDERECO_CONFIRMAR))
    dispatch(setCep(localEndereco.cep))
    dispatch(setTipoLogradouro(localEndereco.tipoLogradouro))
    dispatch(setLogradouro(localEndereco.logradouro))
    dispatch(setBairro(localEndereco.bairro))
    dispatch(setNumero(localEndereco.numero))
    dispatch(setComplemento(localEndereco.complemento))
    dispatch(setUf(localEndereco.uf))
    dispatch(setCidade(localEndereco.cidade))

    Tag.enviarTagueamentoWeb({
      element_action: 'click button',
      element_name: 'confirmar',
      c_page: window.location.href,
      redirect_url: '',
      section_name: 'payrollloan:refin:address',
      section: 'address',
      sub_section: '',
      step: '',
      scenario: 'new',
    })

    dispatch(trackingRefin(StepRefin.ENDERECO))
    dispatch(setHasEndereco(true))

    navigate(getNextRoute())
  }

  useEffect(() => {
    Tag.enviarTagueamentoWeb({
      element_action: 'data_loaded',
      element_name: '',
      c_page: window.location.href,
      redirect_url: '',
      section_name: 'payrollloan:refin:address',
      section: 'address',
      sub_section: '',
      step: '',
      scenario: 'new',
    })
  }, [])

  useEffect(() => {
    if (localEndereco.cep.length !== 10 || showAddressForm) return

    dispatch(
      enderecoRequest({
        cep: cleanCep(localEndereco.cep),
        callback: (data?: EnderecoType) => {
          if (data) {
            setLocalEndereco((prev) => ({
              ...prev,
              tipoLogradouro: data.tipoLogradouro || '',
              logradouro: data.logradouro || '',
              bairro: data.bairro || '',
              numero: '',
              complemento: '',
              uf: data.uf || '',
              cidade: data.cidade ? data.cidade.toUpperCase() : '',
            }))
            setDisabledFields({
              tipoLogradouro: Boolean(data.tipoLogradouro),
              logradouro: Boolean(data.logradouro),
              bairro: Boolean(data.bairro),
              uf: Boolean(data.uf),
              cidade: Boolean(data.cidade),
            })
          }
          setShowAddressForm(true)
        },
      }),
    )
  }, [localEndereco.cep, dispatch, showAddressForm])

  return (
    <Page
      stickyContainerFooterBottom="0px"
      positionFooter="sticky"
      stickyFooter={
        <>
          <Button disabled={isConfirmButtonDisabled()} onClick={handleConfirmClick}>
            Confirmar
          </Button>
          {fromInformacoesPessoais && (
            <Button
              variant="secondary"
              fullWidth
              onClick={() => {
                dispatch(trackingRefin(StepRefin.CLICK_ENDERECO_CANCELAR))
                navigate(routes.INFORMACOES_PESSOAIS)
              }}
              style={{ marginTop: '12px' }}
            >
              Cancelar
            </Button>
          )}
        </>
      }
    >
      <ColorWrapper color={Colors.GRAY500} fontWeight="600" margin="22px 0 16px 0">
        <TextParagraph variant="headline-h2">Cadastre o seu endereço</TextParagraph>
      </ColorWrapper>
      <CustomInput
        id="cep"
        type="text"
        label="Informe seu CEP"
        placeholder="00.000-000"
        maxLength={10}
        value={cepMask(localEndereco.cep)}
        onChange={(e) => {
          const newValue = cepMask((e.target as HTMLInputElement).value)

          if (newValue.length < 10 && showAddressForm) {
            setShowAddressForm(false)
            setLocalEndereco({
              cep: newValue,
              tipoLogradouro: '',
              logradouro: '',
              bairro: '',
              numero: '',
              complemento: '',
              uf: '',
              cidade: '',
            })
            setDisabledFields({
              tipoLogradouro: false,
              logradouro: false,
              bairro: false,
              uf: false,
              cidade: false,
            })
          } else {
            setLocalEndereco((prev) => ({
              ...prev,
              cep: newValue,
            }))
          }
        }}
      />
      {loading ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignContent: 'center',
            justifyContent: 'center',
          }}
        >
          <Loading width={40} height={40} />
        </div>
      ) : (
        <></>
      )}
      {showAddressForm ? (
        <>
          <Select
            key={`tipo-${localEndereco.tipoLogradouro}-${disabledFields.tipoLogradouro}`}
            label="Tipo"
            name="dropdown"
            margin="0 0 20px 0"
            value={transformTipoEnderecoToOption(buildTipoEndereco(), localEndereco.tipoLogradouro)}
            hideSearch={false}
            options={buildTipoEndereco()}
            placeholder="Selecione um tipo"
            disabled={disabledFields.tipoLogradouro}
            onChange={(option: Option) => {
              setLocalEndereco({
                ...localEndereco,
                tipoLogradouro: String(option.value),
              })
            }}
          />
          <CustomInput
            id="logradouro"
            type="text"
            value={localEndereco.logradouro}
            label="Endereço"
            placeholder="Insira o endereço"
            disabled={disabledFields.logradouro}
            onChange={(e) => {
              setLocalEndereco({
                ...localEndereco,
                logradouro: (e.target as HTMLInputElement).value,
              })
            }}
          />
          <Container>
            <CustomInput
              id="numero"
              type="number"
              value={localEndereco.numero}
              label="Número"
              placeholder="Insira o número"
              onlyNumber
              onChange={(e) => {
                setLocalEndereco({
                  ...localEndereco,
                  numero: (e.target as HTMLInputElement).value,
                })
              }}
              style={{ width: '45%' }}
            />
            <CustomInput
              id="complemento"
              type="text"
              value={localEndereco.complemento}
              label="Complemento"
              placeholder="Complemento"
              onChange={(e) => {
                setLocalEndereco({
                  ...localEndereco,
                  complemento: (e.target as HTMLInputElement).value,
                })
              }}
              style={{ width: '50%' }}
            />
          </Container>
          <CustomInput
            id="bairro"
            type="text"
            value={localEndereco.bairro}
            label="Bairro"
            placeholder="Insira o bairro"
            disabled={disabledFields.bairro}
            onChange={(e) => {
              setLocalEndereco({
                ...localEndereco,
                bairro: (e.target as HTMLInputElement).value,
              })
            }}
          />
          <CustomInput
            id="uf"
            type="text"
            value={localEndereco.uf}
            label="Estado"
            placeholder="Insira o estado"
            disabled={disabledFields.uf}
            onChange={(e) => {
              setLocalEndereco({
                ...localEndereco,
                uf: (e.target as HTMLInputElement).value,
              })
            }}
          />
          <CustomInput
            id="cidade"
            type="text"
            value={localEndereco.cidade}
            label="Cidade"
            placeholder="Insira a cidade"
            disabled={disabledFields.cidade}
            onChange={(e) => {
              setLocalEndereco({
                ...localEndereco,
                cidade: (e.target as HTMLInputElement).value,
              })
            }}
          />
        </>
      ) : (
        <></>
      )}
    </Page>
  )
}
export default Endereco
